import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface Vidu15I2vParams {
  /**
   * 输入图像列表，支持URL或base64格式
   */
  images: string[];
  /**
   * 首帧图像，支持URL或base64格式
   */
  first_frame_image?: string;
  /**
   * 提示词，长度不超过1500字符
   */
  prompt?: string;
  /**
   * 风格，可选值：'general'、'anime'
   * @default "general"
   * @values general | anime
   */
  style?: 'general' | 'anime';
  /**
   * 视频时长（秒），可选值：4、8
   * @values 4 | 8
   */
  duration?: 4 | 8;
  /**
   * 分辨率，4秒时可选'360p'、'720p'、'1080p'，8秒时固定'720p'
   */
  resolution?: string;
  /**
   * 随机种子，0表示使用随机数
   * @default 0
   */
  seed?: number;
  /**
   * 宽高比，可选值：'16:9'、'9:16'、'1:1'
   * @default "16:9"
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 运动幅度，可选值：'auto'、'small'、'medium'、'large'
   * @default "auto"
   * @values auto | small | medium | large
   */
  movement_amplitude?: 'auto' | 'small' | 'medium' | 'large';
  /**
   * 是否添加背景音乐
   * @default false
   */
  bgm?: boolean;
  /**
   * 透传参数，最多1048576个字符
   */
  payload?: string;
  /**
   * 回调URL，必须以http://或https://开头
   */
  callback_url?: string;
  /**
   * 是否在非高峰时段处理
   * @default false
   */
  off_peak?: boolean;
}

export interface Vidu15I2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * vidu_15_i2v
 *
 * Vidu 1.5 Image-to-Video generation implementation.
 * 
 * API Documentation: https://platform.vidu.com/docs/image-to-video
 * Status API: https://platform.vidu.com/docs/get-generation
 *
 * @example
 * ```typescript
 * import { initSeacloud, vidu15I2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await vidu15I2v({
 *   // 参数见 Vidu15I2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 Vidu15I2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function vidu15I2v(
  params: Vidu15I2vParams,
): Promise<Vidu15I2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'vidu_15_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: Vidu15I2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
